import absurd from 'common/absurd';
import * as React from 'react';
import FocusTrap from 'src/FocusTrap';
import Layer from 'src/Layer/Layer';
import {Overlay} from 'src/Layer/Overlay';
import type {FocusControls} from 'src/FocusTrap';

type Anchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type Props<RefType extends HTMLElement> = {
  anchorRef: React.RefObject<RefType | null>;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  onOutsideClick?: () => void;
  onOutsideFocus?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  open: boolean;
  onContentBlur?: () => void;
  anchor?: Anchor;
  children?: React.ReactNode;
  // By default the overlay swallows the click that dismisses the popover.
  // With pass-through, outside clicks are detected on pointerdown and still
  // reach their target, so one click both dismisses and activates (e.g. a
  // button elsewhere in the form). Clicks on the anchor keep the popover open.
  passThroughOutsideClicks?: boolean;
};

type Position = {top: number; left: number};

const getPosition = (
  anchor: Anchor,
  triggerRect: DOMRect,
  contentRect: DOMRect,
): Position => {
  switch (anchor) {
    case 'top-left':
      return {
        top: triggerRect.top,
        left: triggerRect.left,
      };
    case 'top-right':
      return {
        top: triggerRect.bottom,
        left: triggerRect.right - contentRect.width,
      };
    case 'bottom-left':
      return {
        top: triggerRect.bottom,
        left: triggerRect.left,
      };
    case 'bottom-right':
      return {
        top: triggerRect.bottom,
        left: triggerRect.right - contentRect.width,
      };
    default:
      return absurd(anchor);
  }
};

export const Popover = <RefType extends HTMLElement>({
  anchorRef,
  anchor = 'top-left',
  children,
  open,
  onOutsideClick,
  onOutsideFocus,
  onContentBlur,
  focusControlsRef,
  passThroughOutsideClicks = false,
}: Props<RefType>) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open || !passThroughOutsideClicks) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      if (contentRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onOutsideClick?.();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, passThroughOutsideClicks, onOutsideClick, anchorRef]);

  const updatePosition = React.useCallback(() => {
    const triggerEl = anchorRef.current;
    const contentEl = contentRef.current;
    if (!triggerEl || !contentEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const contentRect = contentEl.getBoundingClientRect();

    const next = getPosition(anchor, triggerRect, contentRect);

    const viewport = window.visualViewport;
    if (viewport) {
      const margin = 8;

      const minTop = viewport.offsetTop + margin;
      const maxTop =
        viewport.offsetTop + viewport.height - contentRect.height - margin;

      if (maxTop >= minTop) {
        next.top = Math.min(Math.max(next.top, minTop), maxTop);
      }

      const minLeft = viewport.offsetLeft + margin;
      const maxLeft =
        viewport.offsetLeft + viewport.width - contentRect.width - margin;

      if (maxLeft >= minLeft) {
        next.left = Math.min(Math.max(next.left, minLeft), maxLeft);
      }
    }

    const newTop = `${Math.round(next.top * 100) / 100}px`;
    const newLeft = `${Math.round(next.left * 100) / 100}px`;

    if (newTop !== contentEl.style.top || newLeft !== contentEl.style.left) {
      contentEl.style.top = newTop;
      contentEl.style.left = newLeft;
      requestAnimationFrame(updatePosition);
    }
  }, [anchor, anchorRef]);

  React.useLayoutEffect(() => {
    if (!open) return;

    const el = contentRef.current;

    if (!el) return;

    const ro = new ResizeObserver(updatePosition);
    ro.observe(el);

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updatePosition);
    viewport?.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      viewport?.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('scroll', updatePosition);
      ro.disconnect();
    };
  }, [open, updatePosition]);

  if (!open) return null;

  return (
    <Layer>
      <Overlay
        onOverlayClick={passThroughOutsideClicks ? undefined : onOutsideClick}
        interactive={!passThroughOutsideClicks}
      >
        <div
          className="fixed pointer-events-auto"
          ref={contentRef}
          style={{top: '-10000px', left: '-10000px'}}
          onBlur={onContentBlur}
        >
          <FocusTrap
            behavior="continue"
            onFocusOutside={onOutsideFocus}
            focusControlsRef={focusControlsRef}
          >
            {children}
          </FocusTrap>
        </div>
      </Overlay>
    </Layer>
  );
};

export default Popover;
